import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { MortgageSupTypeStrings } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { MatterDocumentType } from '@models/matter-file';
import {
	MatterMortgageType,
	MatterMortgageWithAssociations,
	SYNC_FLOW_TYPES,
} from '@models/mortgage';
import { Select } from '@ngxs/store';
import { VirtualizedMatterFile } from '@state/global/uploaded-documents/upload-documents';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { RequestForFundsSelectors } from '../../closer-sync-rff/request-for-funds.selector';
import {
	PACKAGE_STATUS_TYPES,
	PackageStatus,
	ValidationResults,
} from '../../closer-sync-mortgage/closer-sync-mortgage.model';
import { DetailNavType } from '../../closer-sync-mortgage/closer-sync-mortgage.component';
import { CloserSyncMortgageSelectors } from '../../closer-sync-mortgage/closer-sync-mortgage.selector';
import { BaseComponent } from '@core/base.component';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FundingOptionsDetailsConfig } from '@state/matter/matter_mortgages/contracts/funding-options-details-config';
import { GlobalFormsRepo } from '@repos/global-forms/global-forms.repo';
import { getMortgagePrimaryLender } from '@core/util/mortgage';

enum TAB_TYPES {
	SEND_TO_LENDER = 'send-to-lender',
	PACKAGES = 'packages',
}

const TABS = [
	{
		value: TAB_TYPES.SEND_TO_LENDER,
		title: 'Send to Lender',
		subtitle: 'Edit forms and upload Documents to be sent to the lender.',
		intercomAttr: 'closer-sync-document-tabs-send-to-lender',
	},
	{
		value: TAB_TYPES.PACKAGES,
		title: 'Package History',
		subtitle: 'History of all packages sent to the lender.',
		intercomAttr: 'closer-sync-document-tabs-document-history',
	},
];

const KEBAB_ACTIONS = {
	UPLOAD: 'upload',
	EDIT: 'edit',
	CHANGE_CATEGORY: 'changeCategory',
	HISTORY: 'history',
	DOWNLOAD: 'download',
	DELETE: 'delete',
};

const DocumentsKebabOptions = [
	{
		value: KEBAB_ACTIONS.UPLOAD,
		icon: ICON.UPLOAD,
		classes: ['text-green'],
		name: 'Upload new',
	},
	{
		value: KEBAB_ACTIONS.EDIT,
		icon: ICON.EDIT,
		name: 'Edit name',
	},
	{
		value: KEBAB_ACTIONS.CHANGE_CATEGORY,
		icon: ICON.REPEAT,
		name: 'Change category',
	},
	{
		value: KEBAB_ACTIONS.DOWNLOAD,
		icon: ICON.DOWNLOAD,
		name: 'Download',
	},
	{
		value: KEBAB_ACTIONS.DELETE,
		classes: ['text-red'],
		icon: ICON.DELETE,
		name: 'Delete',
	},
];

interface VM {
	saveTimeSection: string;
	selectedTab: TAB_TYPES;
	isMissingLenderOrCharge: boolean;
	mortgage: MatterMortgageWithAssociations;
	formTemplatesLoaded: boolean;
	hasForms: boolean;
	syncFlowType: SYNC_FLOW_TYPES;
}

const DefaultVM: VM = {
	saveTimeSection: '',
	selectedTab: TAB_TYPES.SEND_TO_LENDER,
	isMissingLenderOrCharge: false,
	mortgage: null,
	formTemplatesLoaded: false,
	hasForms: false,
	syncFlowType: null,
};

@UntilDestroy()
@Component({
	selector: 'app-closer-sync-documents',
	templateUrl: './closer-sync-documents.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncDocumentsComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy, AfterViewInit
{
	@Input() syncFlowType: SYNC_FLOW_TYPES;
	@Output() gotoArea = new EventEmitter<string>();

	TAB_TYPES = TAB_TYPES;
	TABS = TABS;
	SYNC_FLOW_TYPES = SYNC_FLOW_TYPES;

	public DetailNavType = DetailNavType;
	public PACKAGE_STATUS_TYPES = PACKAGE_STATUS_TYPES;
	public ICON = ICON;
	public uploadedDocs: VirtualizedMatterFile[] = [];
	public mortgageDocumentType = MatterDocumentType.MORTGAGE;
	public kebabOptions = DocumentsKebabOptions;
	public mortgageSupTypeStrings = MortgageSupTypeStrings;

	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;
	@Select(SelectedMatter.closingDate) closingDate$: Observable<Date>;
	@Select(RequestForFundsSelectors.GetValidations)
	validations$: Observable<ValidationResults>;

	@Select(CloserSyncMortgageSelectors.getRequestForFundsPackageStatus)
	rffPackageStatus$: Observable<PackageStatus>;
	@Select(CloserSyncMortgageSelectors.isLocalBranchSet)
	isLocalBranchSet$: Observable<boolean>;

	public fundingDetailDocumentValidation: Observable<{
		isMissingRequiredField: boolean;
		hasLocalBranchIfRequired: boolean;
		hasFundingDetails: boolean;
	}>;

	@Select(MatterMortgagesState.fundingOptionsDetailsConfig)
	fundingOptionsDetailsConfig$: Observable<FundingOptionsDetailsConfig>;

	constructor(
		private route: ActivatedRoute,
		private globalMortgageFormsRepo: GlobalFormsRepo,
	) {
		super(DefaultVM);

		this.fundingDetailDocumentValidation = combineLatest([
			this.rffPackageStatus$,
			this.isLocalBranchSet$,
			this.fundingOptionsDetailsConfig$,
		]).pipe(
			map(
				([rffPackageStatus, isLocalBranchSet, fundingOptionsDetailsConfig]) => {
					const hasFundingDetails =
						rffPackageStatus.status !== PACKAGE_STATUS_TYPES.PREPARE;
					// !B || (A && B) => !B || A
					const hasLocalBranchIfRequired =
						!fundingOptionsDetailsConfig?.options?.branchPickupRequired ||
						isLocalBranchSet;

					return {
						hasFundingDetails: hasFundingDetails,
						hasLocalBranchIfRequired: hasLocalBranchIfRequired,
						isMissingRequiredField:
							!hasFundingDetails || !hasLocalBranchIfRequired,
					};
				},
			),
		);
	}

	private validationSubscription;

	ngAfterViewInit() {
		this.route.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
			const tab = params['tab'];

			if (tab) {
				if (tab === 'history') {
					this.selectTab(TAB_TYPES.PACKAGES);
				} else if (tab === 'send-to-lender') {
					this.selectTab(TAB_TYPES.SEND_TO_LENDER);
				}
			}
		});
	}

	ngOnInit(): void {
		this.validationSubscription = this.closingDate$
			.pipe(
				switchMap((closingDate) => {
					if (closingDate && closingDate > new Date()) {
						return this.validations$;
					} else {
						return of(null); // Replace with appropriate value if validations$ is not needed
					}
				}),
			)
			.subscribe((validation) => {
				if (validation && !validation.fundingDetailsValidation.isValid) {
					this.VM = { saveTimeSection: 'fundingDetails' };
				}
			});

		this.mortgage$.pipe(untilDestroyed(this)).subscribe(async (mortgage) => {
			this.VM = {
				isMissingLenderOrCharge:
					!Object.values(mortgage.lenders ?? {})?.length ||
					// If there is no charge type selected, but there are charge types available for the lender
					(!mortgage.chargeType &&
						!!Object.values(
							getMortgagePrimaryLender(mortgage)?.chargeTypes ?? {},
						)?.length),
				mortgage,
				syncFlowType: this.syncFlowType || mortgage?.syncFlowType,
			};

			if (this.VM.syncFlowType === SYNC_FLOW_TYPES.EMAIL) {
				this.TABS = [];
			}

			if (mortgage.type === MatterMortgageType.CLOSER_SYNC) {
				this.VM = {
					formTemplatesLoaded: true,
					hasForms: true,
				};
				return;
			}

			if (
				!this.VM.isMissingLenderOrCharge &&
				this.VM.formTemplatesLoaded === false
			) {
				const lender = getMortgagePrimaryLender(mortgage);

				const formTemplates =
					await this.globalMortgageFormsRepo.getMortgageFormsByLenderChargeType(
						lender?.id,
						mortgage?.chargeType,
					);

				this.VM = {
					formTemplatesLoaded: true,
					hasForms: !!formTemplates?.length,
				};
			}
		});
	}

	ngOnDestroy() {
		this.validationSubscription.unsubscribe();
	}

	selectTab(tab: TAB_TYPES) {
		this.VM = { selectedTab: tab };
	}

	goto(event: Event, path: string) {
		this.gotoArea.emit(path);
	}

	gotoHistory() {
		this.selectTab(TAB_TYPES.PACKAGES);
	}
}
